import React from 'react';
import styled, { css } from 'styled-components';
import type { DefaultTheme } from 'styled-components';

import { getGridUnits as getGridUnitsRaw } from 'utils';
import { MODAL_HEADER_MARGIN_BOTTOM, MODAL_SECTION_MARGIN } from './constants';

// `getGridUnits` in the utils barrel is typed against a legacy theme shape that
// predates the styled-components `DefaultTheme` augmentation; re-type it here so it
// composes inside styled/css template interpolations. Runtime is unchanged.
const getGridUnits = getGridUnitsRaw as unknown as (
  units: number,
) => (props: { theme: DefaultTheme }) => string;

const DataValue = styled.p`
  margin: 0.2em 0;
`;

const DataLabel = styled(DataValue)`
  margin-right: 0.5em;
  font-weight: bold;
`;

interface DataListItemProps {
  label: React.ReactNode;
  value: React.ReactNode;
  className?: string;
}

const DataListItem = styled(({ label, value, ...props }: DataListItemProps) => (
  <div {...props} key={`${label}-${value}`}>
    <DataLabel>{label}:</DataLabel>
    <DataValue>{value}</DataValue>
  </div>
))`
  display: ${({ value }: DataListItemProps) => (value ? 'flex' : 'none')};
`;

const DataListColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

export const DataListHeader = styled.h4`
  margin-bottom: ${getGridUnits(MODAL_HEADER_MARGIN_BOTTOM)};
`;

type DataListEntry = [label: React.ReactNode, value: React.ReactNode];

interface DataListProps {
  title?: React.ReactNode;
  data?: DataListEntry[];
  className?: string;
}

// List Data Points
export const DataList = styled(({ title, data = [], ...props }: DataListProps) => (
  <DataListColumn {...props}>
    {!!title && <DataListHeader>{title}</DataListHeader>}
    {data.map(([label, value]) => (
      <DataListItem key={`${label}-${value}`} label={label} value={value} />
    ))}
  </DataListColumn>
))``;

// Modal Header
export const ModalHeader = styled.h2`
  margin-bottom: ${getGridUnits(MODAL_HEADER_MARGIN_BOTTOM)};
`;

interface ModalSectionProps {
  flex?: number | string;
}

// Modal Section
export const ModalSection = styled.section<ModalSectionProps>`
  flex: ${({ flex = 1 }) => flex};
  display: flex;
  flex-direction: column;
`;

const rowMargin = css`
  margin: 0 0 0 ${getGridUnits(MODAL_SECTION_MARGIN)};
`;
const columnMargin = css`
  margin: ${getGridUnits(MODAL_SECTION_MARGIN)} 0 0 0;
`;

interface ModalSectionWrapperProps {
  direction?: string;
}

// Section Wrapper -- wrap all modal contents that includes <ModalSection />s
export const ModalSectionWrapper = styled.div<ModalSectionWrapperProps>`
  flex: 1;
  display: flex;
  flex-direction: ${({ direction = 'row' }) => direction};

  /* Scoped to DIRECT children only ("> A+A", not "A+A"): without the ">", this compiles to a
     descendant combinator that matches adjacent ModalSections no matter how deeply nested inside
     intermediate wrapper divs, misapplying this margin to ModalSections that aren't actually
     direct siblings within this wrapper's own immediate layout. */
  > ${ModalSection}+${ModalSection} {
    ${({ direction = 'row' }) => (direction === 'column' ? columnMargin : rowMargin)};
  }
`;

// Export user editing component
export { default as UserFieldsEditor } from './user-fields-editor';
